import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays,
  Mail,
  MapPin,
  Phone,
  Users,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

import {
  getHouseholdById,
} from "@/api/households";
import { getHouseholdIntelligence } from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { HouseholdAttendanceCard } from "@/features/households/components/HouseholdAttendanceCard";
import { HouseholdContributorsCard } from "@/features/households/components/HouseholdContributorsCard";
import { HouseholdHealthCard } from "@/features/households/components/HouseholdHealthCard";
import { HouseholdInsightsCard } from "@/features/households/components/HouseholdInsightsCard";
import { HouseholdMembersCard } from "@/features/households/components/HouseholdMembersCard";
import { HouseholdRecommendedActionsCard } from "@/features/households/components/HouseholdRecommendedActionsCard";
import { HouseholdStatisticsCard } from "@/features/households/components/HouseholdStatisticsCard";

import { HouseholdManagement } from "@/features/households/components/HouseholdManagement";

export function HouseholdProfilePage() {
  const { churchId } = useAuth();
  const { permissions } = useEffectivePermissions();
  const { householdId } = useParams();

  const householdQuery = useQuery({
    queryKey: ["household", churchId, householdId],
    enabled: !!churchId && !!householdId,
    queryFn: () => getHouseholdById(churchId!, householdId!),
  });

  const intelligenceQuery = useQuery({
    queryKey: ["household-intelligence", churchId, householdId],
    enabled: !!churchId && !!householdId && permissions.has("INTELLIGENCE_VIEW"),
    queryFn: () => getHouseholdIntelligence(churchId!, householdId!),
  });

  if (householdQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (householdQuery.isError || !householdQuery.data) {
    return (
      <PageContainer>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-6 text-rose-300">
          Unable to load household.
        </div>
      </PageContainer>
    );
  }

  const household = householdQuery.data;
  const intelligence = intelligenceQuery.data?.assessment_status === "ASSESSED" ? intelligenceQuery.data : undefined;

  const location = [household.city, household.state]
    .filter(Boolean)
    .join(", ");

  return (
    <PageContainer>
      <div className="mb-5 flex items-center gap-2 text-sm text-slate-400">
        <Link to="/households" className="hover:text-white">
          Households
        </Link>

        <span>/</span>

        <span className="text-white">
          {household.household_name}
        </span>
      </div>

      <HouseholdManagement household={household} />

      <HouseholdHero
        householdName={household.household_name}
        location={location}
        address={household.address}
        phone={household.phone}
        email={household.email}
        memberCount={permissions.has("MEMBERS_VIEW") ? household.members.length : undefined}
        anniversaryDate={household.anniversary_date}
      />

      {intelligenceQuery.data?.assessment_status === "NOT_ASSESSED" && <p className="text-slate-400">Household intelligence is not yet assessed.</p>}
      {intelligence && (
        <div className="mt-6">
          <HouseholdHealthCard intelligence={intelligence} />
        </div>
      )}

      {intelligence && ["MEMBERS_VIEW", "ATTENDANCE_VIEW", "CARE_VIEW"].every(code => permissions.has(code)) && (
        <div className="mt-6">
          <HouseholdStatisticsCard
            household={household}
            intelligence={intelligence}
          />
        </div>
      )}

      {intelligence && (
        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <HouseholdInsightsCard intelligence={intelligence} />

          <HouseholdRecommendedActionsCard
            intelligence={intelligence}
          />
        </div>
      )}

      {intelligence && ["ATTENDANCE_VIEW", "MEMBERS_VIEW"].every(code => permissions.has(code)) && (
        <div className="mt-6">
          <HouseholdAttendanceCard intelligence={intelligence} />
        </div>
      )}

      {intelligence && ["MEMBERS_VIEW", "ATTENDANCE_VIEW", "CARE_VIEW"].every(code => permissions.has(code)) && (
        <div className="mt-6">
          <HouseholdContributorsCard intelligence={intelligence} />
        </div>
      )}

      {permissions.has("MEMBERS_VIEW") && <div className="mt-6">
        <HouseholdMembersCard
          household={household}
          intelligence={intelligence}
        />
      </div>}

    </PageContainer>
  );
}

function HouseholdHero({
  householdName,
  location,
  address,
  phone,
  email,
  memberCount,
  anniversaryDate,
}: {
  householdName: string;
  location: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  memberCount?: number;
  anniversaryDate?: string | null;
}) {
  const initials = getHouseholdInitials(householdName);

  const fullLocation =
    [address, location].filter(Boolean).join(", ") ||
    "No address on file";

  return (
    <Card className="relative overflow-hidden p-7">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500" />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-center">
        <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-600 text-4xl font-bold text-white shadow-lg shadow-indigo-950/30">
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight text-white">
              {householdName}
            </h1>

            <span className="rounded-full border border-indigo-500/20 bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-300">
              Household
            </span>

            <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300">
              {memberCount === undefined ? "Member information unavailable" : `${memberCount} member${memberCount === 1 ? "" : "s"}`}
            </span>
          </div>

          <p className="mt-2 text-sm text-slate-500">
            Household profile, attendance, and pastoral care context
          </p>

          <div className="mt-5 grid gap-3 text-sm text-slate-300 sm:grid-cols-2 xl:grid-cols-4">
            <HeroDetail
              icon={<MapPin size={17} />}
              label={fullLocation}
            />

            <HeroDetail
              icon={<Phone size={17} />}
              label={phone || "No phone on file"}
            />

            <HeroDetail
              icon={<Mail size={17} />}
              label={email || "No email on file"}
            />

            <HeroDetail
              icon={<Users size={17} />}
              label={memberCount === undefined ? "Member information unavailable" : `${memberCount} household member${memberCount === 1 ? "" : "s"}`}
            />
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/50 px-5 py-4">
            <div className="flex items-center gap-2 text-slate-500">
              <CalendarDays size={17} />

              <span className="text-xs font-medium uppercase tracking-wider">
                Anniversary
              </span>
            </div>

            <p className="mt-2 font-semibold text-white">
              {anniversaryDate
                ? formatDate(anniversaryDate)
                : "Not recorded"}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function HeroDetail({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-slate-500">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
  );
}

function getHouseholdInitials(householdName: string) {
  const words = householdName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return "HH";
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}


